import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { castUnsafe, iff, useAnchorEl, useConst } from "@bryxinc/ts-utils";
import { Accordion, AccordionDetails, AccordionSummary, Popover } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import BryxColors from "../types/theme";
import { salt } from "../utility/object";
import BryxBox from "./BryxBox";
import BryxDropdownButton from "./BryxDropdownButton";
import BryxIcon from "./BryxIcon/BryxIcon";
import BryxSearchBar from "./BryxSearchBar";
import BryxTypography from "./BryxTypography/BryxTypography";
import { useDefer } from "../hooks";
/**
 * Render a {@link BryxNestedUserPopoverItem} leaf node
 */
export function renderItem(item, onClick, inPopover = true) {
    return (_jsx("div", { onClick: inPopover ? onClick : undefined, children: _jsx(BryxBox, { smGap: true, isFlex: true, column: true, fullWidth: true, sx: {
                height: "unset!important",
                alignItems: "flex-start",
                borderRadius: "4px",
            }, children: item.type == "user" ? (_jsxs(BryxBox, { isFlex: true, fullWidth: true, smGap: true, sx: {
                    borderLeft: inPopover ? "1px solid #949494" : "unset",
                    paddingLeft: "16px",
                    paddingBottom: "8px",
                    paddingTop: "8px",
                    "&:hover": inPopover
                        ? {
                            cursor: "pointer",
                            "& *": { color: BryxColors.lightBrand(1) },
                            backgroundColor: BryxColors.lightBrand(3),
                        }
                        : {
                            "& svg": {
                                display: "unset !important",
                            },
                        },
                }, children: [_jsx(BryxBox, { isFlex: true, children: item.iconComponent }), _jsxs(BryxBox, { isFlex: true, fullWidth: true, column: true, children: [_jsx(BryxBox, { isFlex: true, rightJustifyContent: true, fullWidth: true, sx: {
                                    height: 0,
                                    "& svg": { zIndex: 999999 },
                                    "& svg:hover": { cursor: "pointer", display: "unset !important", color: BryxColors.lightBrand(1) },
                                }, children: _jsx(BryxIcon, { disabled: true, smIconSize: true, iconName: "close", onClick: onClick, sx: { display: "none", mt: "10px" } }) }), _jsxs(BryxBox, { isFlex: true, fullWidth: true, sx: { gap: "4px" }, children: [_jsx(BryxTypography, { sx: { textAlign: "left", color: BryxColors.lightNotify("inactive") }, variant: "body1", children: `${item.rank} |` }), _jsx(BryxTypography, { sx: { textAlign: "left", color: BryxColors.lightNotify("active") }, variant: "body1", children: item.name })] }), _jsx(BryxBox, { isFlex: true, fullWidth: true, children: _jsx(BryxTypography, { sx: { textAlign: "left", color: BryxColors.lightNotify("inactive") }, variant: "body0", children: item.email }) })] })] })) : (_jsxs(BryxBox, { isFlex: true, fullWidth: true, column: true, sx: {
                    borderLeft: inPopover ? "1px solid #949494" : "unset",
                    paddingLeft: "16px",
                    paddingBottom: "8px",
                    paddingTop: "8px",
                    "&:hover": inPopover
                        ? {
                            cursor: "pointer",
                            "& *": { color: BryxColors.lightBrand(1) },
                            backgroundColor: BryxColors.lightBrand(3),
                        }
                        : {
                            "& svg": {
                                display: "unset !important",
                            },
                        },
                }, children: [_jsx(BryxBox, { isFlex: true, rightJustifyContent: true, fullWidth: true, sx: {
                            height: 0,
                            "& svg": { zIndex: 999999 },
                            "& svg:hover": { cursor: "pointer", display: "unset !important", color: BryxColors.lightBrand(1) },
                        }, children: _jsx(BryxIcon, { disabled: true, smIconSize: true, iconName: "close", onClick: onClick, sx: { display: "none", mt: "10px" } }) }), _jsx(BryxBox, { isFlex: true, fullWidth: true, children: _jsx(BryxTypography, { sx: { textAlign: "left", color: BryxColors.lightNotify("inactive") }, variant: "body1", children: item.name }) }), _jsx(BryxBox, { isFlex: true, fullWidth: true, children: _jsx(BryxTypography, { sx: { textAlign: "left", color: BryxColors.lightNotify("inactive") }, variant: "body0", children: item.description }) })] })) }) }, item.id));
}
/**
 * A recursive accordian of {@link BryxNestedDropdownAccordionItem} items, and their children
 * @private this component should not be exported
 */
const BryxNestedUserPopoverRecursiveAccordian = (props) => {
    const groupItems = props.items.filter((i) => i.type == "group");
    const personnelItems = props.items.filter((i) => i.type == "user");
    return (_jsxs(_Fragment, { children: [_jsxs(Accordion, { sx: { "&.Mui-expanded": { m: "unset unset 16px unset !important" } }, children: [_jsx(AccordionSummary, { expandIcon: _jsx(BryxIcon, { iconName: "keyboard_arrow_down", smIconSize: true }), sx: {
                            minHeight: "0px !important",
                            "& > div": { m: "12px 0 !important" },
                            "&:hover *": {
                                color: BryxColors.lightBrand(0),
                            },
                            "& .MuiAccordion-region": {
                                borderLeft: "1px solid #949494",
                                marginLeft: "18px",
                            },
                            "& .MuiAccordionDetails-root": {
                                margin: "-16px auto !important",
                            },
                        }, children: "Groups" }), groupItems.map((item) => (_jsx(AccordionDetails, { sx: { p: "0 0 0 16px !important" }, children: renderItem(item, () => props.onSelect(item)) }, item.id)))] }), _jsxs(Accordion, { sx: { "&.Mui-expanded": { m: "unset !important" } }, children: [_jsx(AccordionSummary, { expandIcon: _jsx(BryxIcon, { iconName: "keyboard_arrow_down", smIconSize: true }), sx: {
                            minHeight: "0px !important",
                            "& > div": { m: "12px 0 !important" },
                            "&:hover *": {
                                color: BryxColors.lightBrand(0),
                            },
                        }, children: "Personnel" }), personnelItems.map((item) => (_jsx(AccordionDetails, { sx: { p: "0 0 0 16px !important" }, children: renderItem(item, () => props.onSelect(item)) }, item.id)))] })] }));
};
const BryxNestedUserPopover = (props) => {
    const [anchorEl, setOpen, setClosed] = useAnchorEl();
    const [filter, setFilter] = useState("");
    const open = !!anchorEl;
    const id = useConst(salt(5)); // construct a random, 5 character string
    const deferBlur = useDefer((_) => {
        document.activeElement.blur();
    });
    const flatItemsFiltered = useMemo(() => filter == ""
        ? [] // if no filter, then skip matching routine
        : props.items.filter((item) => 
        // for any relevant value
        (item.type == "user" ? [item.name, item.rank, item.email] : [item.name, item.description]).some((v) => v.toLowerCase().includes(filter.toLowerCase()))), [props.items, filter]);
    useEffect(() => setFilter(""), [open]); // clear search bar on open
    return (_jsxs(_Fragment, { children: [_jsx(BryxTypography, { variant: "body1", sx: { color: BryxColors.lightNotify("inactive"), mb: "8px!important" }, children: props.label }), _jsx(BryxDropdownButton, { active: open, value: "Choose Option", onClick: (e) => setOpen(castUnsafe(e)), error: !!props.error, errorText: props.error }), _jsx(Popover, { id: iff(open, id), open: open, anchorEl: anchorEl, onClose: () => {
                    setClosed();
                    deferBlur(null);
                }, sx: { mt: "8px" }, anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                }, children: _jsxs(BryxBox, { isFlex: true, column: true, sx: { width: "360px", maxHeight: "462px" }, children: [_jsx(BryxBox, { smMargin: true, fullWidth: true, children: _jsx(BryxSearchBar, { hookChange: setFilter, sx: {
                                    "&": { mr: "32px" },
                                    "& .MuiInputBase-root": { mr: "32px" },
                                    "& .MuiInputBase-root, &.MuiFormControl-root": { width: "100% !important" },
                                } }) }), _jsx(BryxBox, { isFlex: true, column: true, sx: { "*": { boxShadow: "none !important" }, mb: "16px" }, children: filter == "" ? (_jsx(BryxNestedUserPopoverRecursiveAccordian, { items: props.items, onSelect: (item) => {
                                    props.onSelect(item);
                                    setClosed();
                                } })) : (_jsx(BryxBox, { xsMargin: true, children: flatItemsFiltered.map((item) => renderItem(item, () => {
                                    props.onSelect(item);
                                    setClosed();
                                })) })) })] }) })] }));
};
export default BryxNestedUserPopover;
